export class TransientDataManager<T extends { id: any }> {
    public isLoading = false;

    private _hasAddedToOriginalSelection = false;
    private _hasRemovedFromOriginalSelection = false;
    private _removalIds = new Set<T['id']>();

    private _options: T[] = [];
    get options(): T[] {
        return this._options;
    }

    private _originalSelection: T[] = [];
    get originalSelection(): T[] {
        return this._originalSelection;
    }

    private _currentSelection: T[] = [];
    get currentSelection(): T[] {
        return this._currentSelection;
    }

    setOptions(opts: T[]) {
        this._options = opts;
    }

    setOriginalSelection(selected: T[]) {
        this._originalSelection = selected;
        this._currentSelection = selected;
        this._removalIds.clear();
        this._hasAddedToOriginalSelection = false;
        this._hasRemovedFromOriginalSelection = false;
    }

    addToCurrentSelection(items: T[]) {
        this._currentSelection = [...items, ...this._currentSelection];
        items.forEach(item => this._removalIds.delete(item.id));
        this._hasAddedToOriginalSelection = this.getAddedToOriginalSelection()?.length > 0;
        this._hasRemovedFromOriginalSelection = this._removalIds.size > 0;
    }

    removeFromCurrentSelection(items: T[]) {
        this._currentSelection = this._currentSelection.filter(
            cur => items.find(item => cur.id === item.id) === undefined
        );
        items.forEach(item => {
            if (this._originalSelection.some(orig => orig.id === item.id)) {
                this._removalIds.add(item.id);
            }
        });
        this._hasAddedToOriginalSelection = this.getAddedToOriginalSelection()?.length > 0;
        this._hasRemovedFromOriginalSelection = this._removalIds.size > 0;
    }

    getAddedToOriginalSelection() {
        return this._currentSelection?.filter(
            cur => this._originalSelection.find(item => cur.id === item.id) === undefined
        );
    }

    // Sourced from the persistent removal-id set so cross-page staged removals
    // survive reloads that replace _originalSelection with a different page.
    getRemovedFromOriginalSelection(): T[] {
        return Array.from(this._removalIds).map(
            id => this._originalSelection.find(item => item.id === id) ?? ({ id } as T)
        );
    }

    hasChanges() {
        return this._hasAddedToOriginalSelection || this._hasRemovedFromOriginalSelection;
    }

    // When preserveCurrentChanges is true, re-baselines _originalSelection to
    // savedSelection without discarding edits staged after the snapshot was
    // captured (typical caller: a save that committed savedSelection as the
    // submitted delta, while the user continued to stage further changes before
    // the PATCH resolved). _currentSelection is left intact, and _removalIds is
    // rebuilt from (a) prior removals still present on the server plus (b) any
    // saved item that is no longer in _currentSelection.
    commitSnapshot(savedSelection: T[], preserveCurrentChanges = false) {
        if (preserveCurrentChanges) {
            const priorRemovalIds = new Set(this._removalIds);
            const savedIds = new Set(savedSelection.map(item => item.id));
            const currentIds = new Set(this._currentSelection.map(item => item.id));

            this._originalSelection = savedSelection;
            this._removalIds = new Set<T['id']>();
            priorRemovalIds.forEach(id => {
                if (savedIds.has(id)) {
                    this._removalIds.add(id);
                }
            });
            savedSelection.forEach(item => {
                if (!currentIds.has(item.id)) {
                    this._removalIds.add(item.id);
                }
            });
        } else {
            this._originalSelection = savedSelection;
            this._removalIds.clear();
        }
        this._hasAddedToOriginalSelection = this.getAddedToOriginalSelection().length > 0;
        this._hasRemovedFromOriginalSelection = this._removalIds.size > 0;
    }

    // Preserves staged additions and staged removals across a reload even when
    // the removed items live on a different page (via the persistent
    // _removalIds set). Dedupes `saved` against additions so an item that is
    // both locally staged and already present server-side doesn't appear twice.
    rebaseOriginalSelection(saved: T[]) {
        const additions = this.getAddedToOriginalSelection();
        const additionIds = new Set(additions.map(a => a.id));
        this._originalSelection = saved;
        this._currentSelection = [
            ...additions,
            ...saved.filter(item => !this._removalIds.has(item.id) && !additionIds.has(item.id))
        ];
        this._hasAddedToOriginalSelection = this.getAddedToOriginalSelection().length > 0;
        this._hasRemovedFromOriginalSelection = this._removalIds.size > 0;
    }

    deselect() {
        this.commitSnapshot(this._currentSelection);
    }

    // Clears staged additions/removals without touching _originalSelection, so
    // the grid can continue rendering the currently loaded baseline while a
    // reload is in flight.
    clearStagedChanges() {
        this._currentSelection = [...this._originalSelection];
        this._removalIds.clear();
        this._hasAddedToOriginalSelection = false;
        this._hasRemovedFromOriginalSelection = false;
    }

    reset() {
        this._originalSelection = [];
        this._currentSelection = [];
        this._removalIds.clear();
        this._hasAddedToOriginalSelection = false;
        this._hasRemovedFromOriginalSelection = false;
    }
}
